import { supabase } from '@/integrations/supabase/client';

export interface CmsMedia {
  id: string;
  filename: string;
  originalName: string;
  filePath: string;
  fileUrl: string;
  fileType: string;
  mimeType: string;
  fileSize: number;
  altText: string | null;
  folderId: string | null;
  usedInPages: string[];
  createdAt: string;
  updatedAt: string;
}

export interface MediaFolder {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  createdAt: string;
}

function mapMedia(item: any): CmsMedia {
  return {
    id: item.id,
    filename: item.filename,
    originalName: item.original_name,
    filePath: item.file_path,
    fileUrl: item.file_url,
    fileType: item.file_type,
    mimeType: item.mime_type,
    fileSize: item.file_size,
    altText: item.alt_text,
    folderId: item.folder_id,
    usedInPages: item.used_in_pages || [],
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

function mapFolder(item: any): MediaFolder {
  return {
    id: item.id,
    name: item.name,
    parentId: item.parent_id,
    sortOrder: item.sort_order,
    createdAt: item.created_at,
  };
}

export async function getMedia(folderId?: string | null): Promise<CmsMedia[]> {
  let query = supabase
    .from('cms_media')
    .select('*')
    .order('created_at', { ascending: false });

  if (folderId === null) {
    query = query.is('folder_id', null);
  } else if (folderId) {
    query = query.eq('folder_id', folderId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapMedia);
}

export async function searchMedia(searchQuery: string): Promise<CmsMedia[]> {
  const { data, error } = await supabase
    .from('cms_media')
    .select('*')
    .or(`original_name.ilike.%${searchQuery}%,alt_text.ilike.%${searchQuery}%`)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapMedia);
}

export async function uploadMedia(
  file: File,
  folderId?: string | null,
  altText?: string
): Promise<CmsMedia> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
  const filePath = `cms/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('images')
    .upload(filePath, file);

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('images')
    .getPublicUrl(filePath);

  const { data, error } = await supabase
    .from('cms_media')
    .insert({
      filename: fileName,
      original_name: file.name,
      file_path: filePath,
      file_url: urlData.publicUrl,
      file_type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file',
      mime_type: file.type,
      file_size: file.size,
      alt_text: altText || null,
      folder_id: folderId || null,
      used_in_pages: [],
    })
    .select()
    .single();

  if (error) throw error;
  return mapMedia(data);
}

export async function updateMediaMeta(
  id: string,
  altText?: string,
  folderId?: string | null
): Promise<CmsMedia> {
  const updates: any = { updated_at: new Date().toISOString() };
  if (altText !== undefined) updates.alt_text = altText;
  if (folderId !== undefined) updates.folder_id = folderId;

  const { data, error } = await supabase
    .from('cms_media')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return mapMedia(data);
}

export async function deleteMedia(id: string): Promise<void> {
  const { data: mediaData, error: fetchError } = await supabase
    .from('cms_media')
    .select('file_path')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  await supabase.storage.from('images').remove([mediaData.file_path]);

  const { error } = await supabase.from('cms_media').delete().eq('id', id);
  if (error) throw error;
}

export async function bulkDeleteMedia(ids: string[]): Promise<void> {
  for (const id of ids) {
    await deleteMedia(id);
  }
}

export async function getFolders(): Promise<MediaFolder[]> {
  const { data, error } = await supabase
    .from('media_folders')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapFolder);
}

export async function createFolder(name: string, parentId?: string | null): Promise<MediaFolder> {
  const { data, error } = await supabase
    .from('media_folders')
    .insert({
      name,
      parent_id: parentId || null,
    })
    .select()
    .single();

  if (error) throw error;
  return mapFolder(data);
}

export async function renameFolder(id: string, name: string): Promise<MediaFolder> {
  const { data, error } = await supabase
    .from('media_folders')
    .update({ name })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return mapFolder(data);
}

export async function deleteFolder(id: string): Promise<void> {
  const { error } = await supabase.from('media_folders').delete().eq('id', id);
  if (error) throw error;
}
